import {
    AllocatedUpgradesTableColumnId,
    ClusterProjectsTableColumnId,
    ConstraintsTableColumnId,
    ScenarioProjectsTableColumnId,
    ScenariosTableColumnId
} from "in_queue/components/tables/agGrid/agGridHelpers";
import { RegionType } from "types/regionType";
import { Cluster, StudyCycle, StudyGroup, StudyPhase } from "./clusterType";
import { CostAssumptionType, ScenarioStage } from "./scenarioType";
import { SensitivityType } from "./sensitivityType";

type TableConfig<TColumn> = {
    columnsToRemove: readonly TColumn[];
};

type SizeLabel = {
    energyLabel: string;
    capacityLabel: string;
};

type LoadLabel = {
    benchLoad: string;
    studyLoad: string;
};

type CustomUpgradesConfig = {
    enabled: boolean;
    // Upload one change file per model variant (PJM ships an IDV per season)
    // instead of a single file for the whole upgrade.
    modelSpecificCustomUpgrades?: boolean;
};

export type StageConfig = {
    maxMwPercentageRemoved: number;
    availableSensitivityOptions: readonly SensitivityType[];
    scenarioWarningMsg: string | null;
    showProjectStatus: boolean;
    supportedCostAssumptionTypes: Record<ScenarioStage, CostAssumptionType[]>;
    enableSpeculativeProjects: boolean;
};

const DEFAULT_MISO_WITH_SUGAR_COST_ASSUMPTIONS = {
    PREVIEW: [CostAssumptionType.SUGAR_UPGRADES],
    DECISION_POINT: [
        CostAssumptionType.NETWORK_UPGRADES,
        CostAssumptionType.SUGAR_UPGRADES
    ]
};

const DEFAULT_STAGE_CONFIG: StageConfig = {
    maxMwPercentageRemoved: 0.99,
    availableSensitivityOptions: [
        SensitivityType.EXCLUSION,
        SensitivityType.DOWNSIZE
    ] as const,
    scenarioWarningMsg: null,
    showProjectStatus: false,
    enableSpeculativeProjects: false,

    // By default we only show cost assumption options for preview, because for DP
    // we default to using ISO-provided NUs
    supportedCostAssumptionTypes: {
        PREVIEW: [CostAssumptionType.CONSERVATIVE, CostAssumptionType.CUSTOM],
        DECISION_POINT: []
    }
};

type AvailableClusters = {
    readonly [key in StudyCycle]?: {
        readonly [key in StudyGroup]?: {
            readonly [key in StudyPhase]?: StageConfig;
        };
    };
};

export enum RelatedProjectsTypeEnum {
    SharedAllocations = "sharedAllocations",
    SharedMonitoredFacilities = "sharedMonitoredFacilities"
}

export type InQueueRegionConfig = {
    readonly scenariosTable: TableConfig<ScenariosTableColumnId>;
    readonly allocatedUpgradesTable: TableConfig<AllocatedUpgradesTableColumnId>;
    readonly constraintsTable: TableConfig<ConstraintsTableColumnId>;
    readonly clusterProjectsTable: TableConfig<ClusterProjectsTableColumnId>;
    readonly scenarioProjectsTable: TableConfig<ScenarioProjectsTableColumnId>;
    readonly sizeLabel: SizeLabel;
    readonly loadLabel: LoadLabel;
    readonly maxDfaxLabel: string;
    // Header for the constraints-tab study-cycle column. MISO: "Study Cycle"; PJM: "Cluster".
    readonly studyCycleLabel: string;
    // Custom upgrades are gated per region, so every cluster in an enabled
    // region offers them; the cluster subscription's add-on is the other gate.
    readonly customUpgrades: CustomUpgradesConfig;
    readonly availableClusters: AvailableClusters;
    readonly relatedProjectsType: RelatedProjectsTypeEnum;
    // MISO-only scenario-builder sizing rules: prior-queue (cross-cycle) ERIS is
    // withdraw-only, withdrawn prior-queue projects are removed up front, and a
    // reduction that leaves a current-cycle project with more NRIS MW than ERIS
    // MW warns first (NRIS includes an equivalent amount of ERIS). Other regions
    // resize freely.
    readonly strictSizeRulesEnabled: boolean;
};

const DEFAULT_CONFIG: InQueueRegionConfig = {
    scenariosTable: { columnsToRemove: [] },
    allocatedUpgradesTable: { columnsToRemove: [] },
    constraintsTable: { columnsToRemove: [] },
    clusterProjectsTable: { columnsToRemove: [] },
    scenarioProjectsTable: { columnsToRemove: [] },
    sizeLabel: {
        energyLabel: "Energy",
        capacityLabel: "Capacity"
    },
    loadLabel: {
        benchLoad: "Bench",
        studyLoad: "Study"
    },
    maxDfaxLabel: "Max Dfax",
    studyCycleLabel: "Study Cycle",
    customUpgrades: {
        enabled: false
    },
    availableClusters: {},
    relatedProjectsType: RelatedProjectsTypeEnum.SharedAllocations,
    strictSizeRulesEnabled: false
};

const REGIONS_TO_COMPONENT_CONFIG: {
    readonly [key in RegionType]: InQueueRegionConfig;
} = {
    [RegionType.NYISO]: {
        ...DEFAULT_CONFIG,
        clusterProjectsTable: { columnsToRemove: ["hybridLimit"] },
        scenariosTable: { columnsToRemove: ["hybridLimit"] },
        constraintsTable: {
            columnsToRemove: [
                "model",
                "preStudyLoad",
                "preStudyFlow",
                "postStudyFlow"
            ]
        },
        scenarioProjectsTable: {
            columnsToRemove: [
                "subregion",
                "erisThermal",
                "erisComplex",
                "nrisThermal",
                "nrisComplex",
                "erisSnu",
                "erisLpc",
                "group",
                "hybridLimit",
                "erisSize",
                "jtiq"
            ]
        },
        availableClusters: {
            [StudyCycle.NYISO_CY_2024]: {
                [StudyGroup.DEFAULT_GROUP]: {
                    [StudyPhase.PHASE_ONE]: {
                        ...DEFAULT_STAGE_CONFIG,
                        maxMwPercentageRemoved: 0.7,
                        availableSensitivityOptions: [
                            SensitivityType.EXCLUSION
                        ],
                        supportedCostAssumptionTypes: {
                            PREVIEW: [
                                CostAssumptionType.CONSERVATIVE,
                                CostAssumptionType.CUSTOM
                            ],
                            DECISION_POINT: []
                        }
                    },
                    [StudyPhase.PHASE_TWO]: {
                        ...DEFAULT_STAGE_CONFIG,
                        maxMwPercentageRemoved: 0.7,
                        availableSensitivityOptions: [
                            SensitivityType.EXCLUSION
                        ],
                        supportedCostAssumptionTypes: {
                            PREVIEW: [
                                CostAssumptionType.CONSERVATIVE,
                                CostAssumptionType.CUSTOM
                            ],
                            DECISION_POINT: []
                        }
                    }
                }
            }
        }
    },
    [RegionType.PJM]: {
        ...DEFAULT_CONFIG,
        // PJM refers to its study cycles as "Clusters" (TC2, Cycle 1); MISO keeps "Study Cycle".
        studyCycleLabel: "Cluster",
        clusterProjectsTable: { columnsToRemove: ["hybridLimit"] },
        scenariosTable: {
            columnsToRemove: ["inputData", "costAssumptions", "hybridLimit"]
        },
        allocatedUpgradesTable: {
            columnsToRemove: ["maxDfax", "sumMwImpact", "constraintType"]
        },
        constraintsTable: {
            columnsToRemove: [
                "preStudyFlow",
                "postStudyFlow",
                "preStudyLoad",
                "facilityDetails",
                "area"
            ]
        },
        scenarioProjectsTable: {
            columnsToRemove: [
                "subregion",
                "erisThermal",
                "erisComplex",
                "nrisThermal",
                "nrisComplex",
                "erisSnu",
                "erisLpc",
                "group",
                "hybridLimit",
                "jtiq"
            ]
        },
        sizeLabel: {
            energyLabel: "Energy",
            capacityLabel: "Capacity"
        },
        loadLabel: {
            benchLoad: "Pre-cluster",
            studyLoad: "Post-cluster"
        },
        customUpgrades: {
            enabled: true,
            modelSpecificCustomUpgrades: true
        },
        availableClusters: {
            [StudyCycle.PJM_TC1]: {
                [StudyGroup.DEFAULT_GROUP]: {
                    [StudyPhase.PHASE_ONE]: {
                        ...DEFAULT_STAGE_CONFIG,
                        supportedCostAssumptionTypes: {
                            PREVIEW: [],
                            DECISION_POINT: []
                        }
                    },
                    [StudyPhase.PHASE_TWO]: {
                        ...DEFAULT_STAGE_CONFIG,
                        supportedCostAssumptionTypes: {
                            PREVIEW: [],
                            DECISION_POINT: []
                        }
                    },
                    [StudyPhase.PHASE_THREE]: {
                        ...DEFAULT_STAGE_CONFIG,
                        supportedCostAssumptionTypes: {
                            PREVIEW: [],
                            DECISION_POINT: []
                        }
                    }
                }
            },
            [StudyCycle.PJM_TC2]: {
                [StudyGroup.DEFAULT_GROUP]: {
                    [StudyPhase.RESUBMISSION]: {
                        ...DEFAULT_STAGE_CONFIG
                    },
                    [StudyPhase.PHASE_ONE]: {
                        ...DEFAULT_STAGE_CONFIG
                    },
                    [StudyPhase.PHASE_TWO]: {
                        ...DEFAULT_STAGE_CONFIG
                    }
                }
            },
            [StudyCycle.PJM_C1]: {
                [StudyGroup.DEFAULT_GROUP]: {
                    [StudyPhase.RESUBMISSION]: {
                        ...DEFAULT_STAGE_CONFIG,
                        enableSpeculativeProjects: true
                    }
                }
            }
        },
        relatedProjectsType: RelatedProjectsTypeEnum.SharedMonitoredFacilities
    },
    [RegionType.MISO]: {
        ...DEFAULT_CONFIG,
        strictSizeRulesEnabled: true,
        clusterProjectsTable: { columnsToRemove: ["hybridLimit"] },
        scenariosTable: { columnsToRemove: ["hybridLimit"] },
        sizeLabel: {
            energyLabel: "ERIS",
            capacityLabel: "NRIS"
        },
        allocatedUpgradesTable: {
            columnsToRemove: ["networkUpgradeId"]
        },
        constraintsTable: {
            columnsToRemove: []
        },
        scenarioProjectsTable: {
            columnsToRemove: ["group", "hybridLimit", "jtiq"]
        },
        customUpgrades: {
            enabled: true
        },
        availableClusters: {
            [StudyCycle.MISO_2025]: {
                [StudyGroup.DEFAULT_GROUP]: {
                    [StudyPhase.PRESCREEN]: {
                        ...DEFAULT_STAGE_CONFIG,
                        maxMwPercentageRemoved: 0.85,
                        supportedCostAssumptionTypes:
                            DEFAULT_MISO_WITH_SUGAR_COST_ASSUMPTIONS
                    },
                    [StudyPhase.PHASE_ONE]: {
                        ...DEFAULT_STAGE_CONFIG,
                        maxMwPercentageRemoved: 0.85,
                        supportedCostAssumptionTypes:
                            DEFAULT_MISO_WITH_SUGAR_COST_ASSUMPTIONS
                    },
                    [StudyPhase.PHASE_TWO]: {
                        ...DEFAULT_STAGE_CONFIG,
                        maxMwPercentageRemoved: 0.85,
                        supportedCostAssumptionTypes:
                            DEFAULT_MISO_WITH_SUGAR_COST_ASSUMPTIONS
                    }
                }
            },
            [StudyCycle.MISO_2023]: {
                [StudyGroup.DEFAULT_GROUP]: {
                    [StudyPhase.PRESCREEN]: {
                        ...DEFAULT_STAGE_CONFIG,
                        maxMwPercentageRemoved: 0.9
                    },
                    [StudyPhase.PHASE_ONE]: {
                        ...DEFAULT_STAGE_CONFIG,
                        showProjectStatus: true,
                        supportedCostAssumptionTypes:
                            DEFAULT_MISO_WITH_SUGAR_COST_ASSUMPTIONS
                    },
                    [StudyPhase.PHASE_TWO]: {
                        ...DEFAULT_STAGE_CONFIG,
                        showProjectStatus: true,
                        supportedCostAssumptionTypes:
                            DEFAULT_MISO_WITH_SUGAR_COST_ASSUMPTIONS
                    }
                }
            },
            [StudyCycle.MISO_2022]: {
                [StudyGroup.MISO_SOUTH]: {
                    [StudyPhase.RESUBMISSION]: {
                        ...DEFAULT_STAGE_CONFIG
                    },
                    [StudyPhase.PHASE_ONE]: {
                        ...DEFAULT_STAGE_CONFIG,
                        maxMwPercentageRemoved: 0.8
                    },
                    [StudyPhase.PHASE_TWO]: {
                        ...DEFAULT_STAGE_CONFIG,
                        showProjectStatus: true,
                        supportedCostAssumptionTypes:
                            DEFAULT_MISO_WITH_SUGAR_COST_ASSUMPTIONS
                    }
                },
                [StudyGroup.MISO_CENTRAL]: {
                    [StudyPhase.PHASE_ONE]: {
                        ...DEFAULT_STAGE_CONFIG,
                        maxMwPercentageRemoved: 0.8
                    },
                    [StudyPhase.PHASE_TWO]: {
                        ...DEFAULT_STAGE_CONFIG,
                        showProjectStatus: true,
                        supportedCostAssumptionTypes:
                            DEFAULT_MISO_WITH_SUGAR_COST_ASSUMPTIONS
                    }
                },
                [StudyGroup.MISO_WEST]: {
                    [StudyPhase.PHASE_ONE]: {
                        ...DEFAULT_STAGE_CONFIG,
                        maxMwPercentageRemoved: 0.8
                    },
                    [StudyPhase.PHASE_TWO]: {
                        ...DEFAULT_STAGE_CONFIG,
                        showProjectStatus: true,
                        supportedCostAssumptionTypes:
                            DEFAULT_MISO_WITH_SUGAR_COST_ASSUMPTIONS
                    }
                },
                [StudyGroup.MISO_ATC]: {
                    [StudyPhase.PHASE_ONE]: {
                        ...DEFAULT_STAGE_CONFIG,
                        maxMwPercentageRemoved: 0.8
                    },
                    [StudyPhase.PHASE_TWO]: {
                        ...DEFAULT_STAGE_CONFIG,
                        showProjectStatus: true,
                        supportedCostAssumptionTypes:
                            DEFAULT_MISO_WITH_SUGAR_COST_ASSUMPTIONS
                    }
                },
                [StudyGroup.MISO_ITC]: {
                    [StudyPhase.PHASE_ONE]: {
                        ...DEFAULT_STAGE_CONFIG,
                        maxMwPercentageRemoved: 0.8
                    },
                    [StudyPhase.PHASE_TWO]: {
                        ...DEFAULT_STAGE_CONFIG,
                        showProjectStatus: true,
                        supportedCostAssumptionTypes:
                            DEFAULT_MISO_WITH_SUGAR_COST_ASSUMPTIONS
                    }
                }
            },
            [StudyCycle.MISO_2021]: {
                [StudyGroup.MISO_SOUTH]: {
                    [StudyPhase.PHASE_ONE]: DEFAULT_STAGE_CONFIG,
                    [StudyPhase.PHASE_TWO]: DEFAULT_STAGE_CONFIG,
                    [StudyPhase.PHASE_THREE]: DEFAULT_STAGE_CONFIG
                },
                [StudyGroup.MISO_CENTRAL]: {
                    [StudyPhase.PHASE_ONE]: DEFAULT_STAGE_CONFIG,
                    [StudyPhase.PHASE_TWO]: {
                        ...DEFAULT_STAGE_CONFIG,
                        showProjectStatus: true
                    }
                },
                [StudyGroup.MISO_ATC]: {
                    [StudyPhase.PHASE_TWO]: DEFAULT_STAGE_CONFIG
                },
                [StudyGroup.MISO_ITC]: {
                    [StudyPhase.PHASE_TWO]: DEFAULT_STAGE_CONFIG
                },
                [StudyGroup.MISO_WEST]: {
                    [StudyPhase.PHASE_TWO]: DEFAULT_STAGE_CONFIG
                }
            },
            [StudyCycle.MISO_2020]: {
                [StudyGroup.MISO_CENTRAL]: {
                    [StudyPhase.PHASE_ONE]: DEFAULT_STAGE_CONFIG,
                    [StudyPhase.PHASE_TWO]: DEFAULT_STAGE_CONFIG,
                    [StudyPhase.PHASE_THREE]: DEFAULT_STAGE_CONFIG
                }
            }
        }
    },
    [RegionType.CAISO]: {
        ...DEFAULT_CONFIG,
        constraintsTable: { columnsToRemove: ["model"] }
    },
    [RegionType.SPP]: {
        ...DEFAULT_CONFIG,
        scenariosTable: {
            columnsToRemove: ["inputData", "costAssumptions"]
        },
        allocatedUpgradesTable: {
            columnsToRemove: [
                "sumMwImpact",
                "constraintType",
                "networkUpgradeId"
            ]
        },
        sizeLabel: {
            energyLabel: "ERIS",
            capacityLabel: "NRIS"
        },
        maxDfaxLabel: "PTDF",
        scenarioProjectsTable: {
            columnsToRemove: [
                "subregion",
                "erisThermal",
                "erisComplex",
                "nrisThermal",
                "nrisComplex",
                "erisSnu",
                "erisLpc",
                "jtiq"
            ]
        },
        constraintsTable: {
            columnsToRemove: [
                "preStudyFlow",
                "preStudyLoad",
                "facilityDetails",
                "model",
                "area"
            ]
        },
        availableClusters: {
            [StudyCycle.SPP_2022]: {
                [StudyGroup.DEFAULT_GROUP]: {
                    [StudyPhase.PHASE_TWO]: DEFAULT_STAGE_CONFIG
                }
            },
            DISIS_2023: {
                default_group: {
                    two: DEFAULT_STAGE_CONFIG
                }
            },
            [StudyCycle.SPP_2024]: {
                [StudyGroup.DEFAULT_GROUP]: {
                    [StudyPhase.PHASE_ONE]: DEFAULT_STAGE_CONFIG
                }
            }
        }
    },
    [RegionType.ERCOT]: {
        ...DEFAULT_CONFIG,
        constraintsTable: { columnsToRemove: ["model"] }
    },
    [RegionType.WECC]: {
        ...DEFAULT_CONFIG,
        constraintsTable: { columnsToRemove: ["model"] }
    },
    [RegionType.TVA]: {
        ...DEFAULT_CONFIG,
        constraintsTable: { columnsToRemove: ["model"] }
    },
    [RegionType.ISONE]: {
        ...DEFAULT_CONFIG,
        constraintsTable: { columnsToRemove: ["model"] }
    },
    [RegionType.SOCO]: {
        ...DEFAULT_CONFIG,
        constraintsTable: { columnsToRemove: ["model"] }
    },
    [RegionType.DUKE]: {
        ...DEFAULT_CONFIG,
        constraintsTable: { columnsToRemove: ["model"] }
    }
};

export const getRegionConfig = (region: RegionType) => {
    return REGIONS_TO_COMPONENT_CONFIG[region];
};

export const getStageConfig = (cluster: Cluster) => {
    const componentConfig = getRegionConfig(cluster.region);
    const stageConfig =
        componentConfig.availableClusters[cluster.studyCycle]?.[
            cluster.studyGroup
        ]?.[cluster.studyPhase] ?? DEFAULT_STAGE_CONFIG;
    return stageConfig;
};

/** Whether the stage allows only exclusion, so a project is in at full size or out. */
export const isOnlyExclusionSupported = (cluster: Cluster): boolean => {
    const { availableSensitivityOptions } = getStageConfig(cluster);
    return (
        availableSensitivityOptions.length === 1 &&
        availableSensitivityOptions.includes(SensitivityType.EXCLUSION)
    );
};
